/**
 * TimelineTracker — calendar highlights and deadline alerts for notification tracking.
 */

import { useMemo, useState, useEffect } from 'react';
import type { TrackedNotification } from '../../utils/notificationTrackingTypes';
import { buildDeadlineAlerts, parseISODate } from '../../utils/notificationDeadlineUtils';
import { Calendar, AlertTriangle, Clock } from 'lucide-react';

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export interface TimelineTrackerProps {
  institutions: TrackedNotification[];
  /** ISO yyyy-mm-dd — which month the calendar opens on */
  focusDate?: string;
  onFocusDateChange?: (iso: string) => void;
  className?: string;
}

export default function TimelineTracker({
  institutions,
  focusDate,
  onFocusDateChange,
  className = '',
}: TimelineTrackerProps) {
  const initial = useMemo(() => {
    const fd = focusDate ? parseISODate(focusDate) : null;
    return fd || new Date();
  }, [focusDate]);

  const [cursor, setCursor] = useState(() => startOfMonth(initial));

  useEffect(() => {
    if (focusDate) {
      const fd = parseISODate(focusDate);
      if (fd) setCursor(startOfMonth(fd));
    }
  }, [focusDate]);

  const alerts = useMemo(() => buildDeadlineAlerts(institutions), [institutions]);

  const deadlineDaysInMonth = useMemo(() => {
    const set = new Set<number>();
    for (const inst of institutions) {
      if (!inst.deadlineDate) continue;
      const d = parseISODate(inst.deadlineDate);
      if (!d) continue;
      if (d.getFullYear() === cursor.getFullYear() && d.getMonth() === cursor.getMonth()) {
        set.add(d.getDate());
      }
    }
    return set;
  }, [institutions, cursor]);

  const monthLabel = cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const firstDow = startOfMonth(cursor).getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);

  const shiftMonth = (delta: number) => {
    const next = addMonths(cursor, delta);
    setCursor(next);
    const iso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`;
    onFocusDateChange?.(iso);
  };

  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2 text-slate-800">
          <Calendar className="w-5 h-5 text-teal-600" aria-hidden />
          <h3 className="text-sm font-semibold tracking-wide uppercase">Deadlines & calendar</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => shiftMonth(-1)}
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="text-sm font-medium text-slate-800 min-w-[10rem] text-center">{monthLabel}</span>
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => shiftMonth(1)}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="px-4 py-3 space-y-2 bg-amber-50/80 border-b border-amber-100">
          <div className="text-xs font-semibold uppercase text-amber-900 tracking-wide">Active reminders</div>
          {alerts.slice(0, 6).map((a) => (
            <div key={a.id} className="flex items-start gap-2 text-sm text-amber-950">
              {a.level === 'overdue' ? (
                <AlertTriangle className="w-4 h-4 shrink-0 text-red-600 mt-0.5" aria-hidden />
              ) : (
                <Clock className="w-4 h-4 shrink-0 text-amber-700 mt-0.5" aria-hidden />
              )}
              <span>
                <span className="font-medium">{a.name}</span>
                {' — '}
                {a.level === 'overdue'
                  ? `overdue (${Math.abs(a.days)} day${Math.abs(a.days) === 1 ? '' : 's'})`
                  : `due in ${a.days} day${a.days === 1 ? '' : 's'}`}
                {' '}
                <span className="text-amber-800/90">({a.deadlineDate})</span>
              </span>
            </div>
          ))}
          {alerts.length > 6 && (
            <div className="text-xs text-amber-900">+{alerts.length - 6} more not shown — set deadlines on rows below.</div>
          )}
        </div>
      )}

      <div className="p-4">
        <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="py-1 font-medium">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, idx) => (
            <div
              key={idx}
              className={`min-h-[2.25rem] flex flex-col items-center justify-center rounded-lg text-sm border border-transparent ${
                day && deadlineDaysInMonth.has(day)
                  ? 'bg-teal-50 border-teal-200 text-teal-900 font-semibold'
                  : day
                    ? 'text-slate-700 hover:bg-slate-50'
                    : ''
              }`}
            >
              {day ?? ''}
              {day && deadlineDaysInMonth.has(day) && (
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-0.5" aria-hidden />
              )}
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Dates with dots match institution deadlines you set in the table. This is for executor coordination only and
          does not send external notifications.
        </p>
      </div>
    </div>
  );
}
